/**
 * AccountPage Component
 * Page for viewing subscription and account information
 * 
 * Requirements: 6.1, 6.3, 6.4, 6.5, 7.2, 7.3
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './AccountPage.css';
import SubscriptionTiers from '../components/SubscriptionTiers';
import { Skeleton } from '../components/SkeletonScreen';
import { SubscriptionService } from '../services/SubscriptionService';
import { useTutorial } from '../components/TutorialProvider';
import ThriftLinkCard from '../components/ThriftLinkCard';
import type { Subscription, SubscriptionTier } from '../types';

function AccountPage() {
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [tiers, setTiers] = useState<SubscriptionTier[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [subscriptionService] = useState(() => new SubscriptionService());
  const [isBuyCreditsModalOpen, setIsBuyCreditsModalOpen] = useState(false);
  const [creditsToBuy, setCreditsToBuy] = useState(100);
  const [isBuyingCredits, setIsBuyingCredits] = useState(false);
  const [isRestartingTutorial, setIsRestartingTutorial] = useState(false);
  
  // Tutorial context
  const { resetTutorial } = useTutorial();

  // Fetch subscription data on page load (Requirement 6.1)
  useEffect(() => {
    loadSubscriptionData();
  }, []);

  const loadSubscriptionData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [subscriptionData, tiersData] = await Promise.all([
        subscriptionService.getSubscription(),
        subscriptionService.getTiers(),
      ]);
      setSubscription(subscriptionData);
      setTiers(tiersData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load subscription data';
      setError(errorMessage);
      console.error('Error loading subscription data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle upgrade button click (Requirement 7.2, 7.3)
  const handleUpgrade = async (tier: SubscriptionTier) => {
    setIsUpgrading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Create checkout session
      const successUrl = `${window.location.origin}/account?success=true`;
      const cancelUrl = `${window.location.origin}/account?cancelled=true`;

      const { checkout_url } = await subscriptionService.createCheckoutSession(
        tier.name.toLowerCase(),
        successUrl,
        cancelUrl
      );

      // Redirect to Stripe checkout
      window.location.href = checkout_url;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create checkout session';
      setError(errorMessage);
      console.error('Error creating checkout session:', err);
      setIsUpgrading(false);
    }
  };

  // Handle subscription cancellation (Requirement 6.4)
  const handleCancelSubscription = async () => {
    const confirmed = window.confirm(
      'Are you sure you want to cancel your subscription? You will lose access to premium features at the end of your billing period.'
    );

    if (!confirmed) return;

    setIsCancelling(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await subscriptionService.cancelSubscription();
      setSuccessMessage('Subscription cancelled successfully. You will retain access until the end of your billing period.');

      // Reload subscription data to reflect changes
      await loadSubscriptionData();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to cancel subscription';
      setError(errorMessage);
      console.error('Error cancelling subscription:', err);
    } finally {
      setIsCancelling(false);
    }
  };

  // Check URL parameters for checkout success/cancel
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'true') {
      setSuccessMessage('Subscription upgraded successfully! Your new plan is now active.');
      // Clean up URL
      window.history.replaceState({}, '', '/account');
      // Reload subscription data
      loadSubscriptionData();
    } else if (params.get('cancelled') === 'true') {
      setError('Checkout was cancelled. No changes were made to your subscription.');
      // Clean up URL
      window.history.replaceState({}, '', '/account');
    } else if (params.get('credits_success') === 'true') {
      setSuccessMessage('Credits purchased successfully! Your balance has been updated.');
      // Clean up URL
      window.history.replaceState({}, '', '/account');
      // Reload subscription data
      loadSubscriptionData();
    } else if (params.get('credits_cancelled') === 'true') {
      setError('Credit purchase was cancelled. No charges were made.');
      // Clean up URL
      window.history.replaceState({}, '', '/account');
    } else if (params.get('thrift_linked') === 'true') {
      setSuccessMessage('THRIFT account linked successfully! You can now push results to THRIFT and use AI features on THRIFT.');
      // Clean up URL
      window.history.replaceState({}, '', '/account');
    }
  }, []);

  if (isLoading) {
    return (
      <div className="account-page">
        <div className="account-container">
          <div className="page-header">
            <Skeleton variant="text" width="60%" height={32} />
            <Skeleton variant="text" width="80%" height={20} />
          </div>
          <div className="subscription-skeleton">
            <Skeleton variant="rectangular" width="100%" height={150} />
            <Skeleton variant="rectangular" width="100%" height={100} />
            <div className="tiers-skeleton">
              {Array.from({ length: 3 }).map((_, index) => (
                <Skeleton key={index} variant="rectangular" width="100%" height={200} />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="account-page">
        <div className="account-container">
          <div className="error-message" role="alert">
            <span className="error-icon">⚠️</span>
            <span>Failed to load subscription information. Please try again.</span>
            <button className="retry-btn" onClick={loadSubscriptionData}>
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const hasActiveSubscription = 
    subscription.status === 'active' && 
    subscription.subscription_type !== 'free' &&
    subscription.stripe_subscription_id;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const creditsPercentage = subscription.credits_limit > 0 
    ? (subscription.credits_remaining / subscription.credits_limit) * 100 
    : 0;

  const isLowCredits = creditsPercentage < 20;
  
  const subscriptionType = String(subscription.subscription_type || 'Unknown');

  // Get cost per credit based on subscription tier
  const currentTier = tiers.find(t => t.name.toLowerCase() === subscriptionType.toLowerCase());
  
  // Calculate cost per credit from monthly price and credits if not provided
  let costPerCredit = currentTier?.cost_per_credit || 0;
  if (costPerCredit === 0 && currentTier?.price_monthly && currentTier?.credits_per_month) {
    costPerCredit = currentTier.price_monthly / currentTier.credits_per_month;
  }
  if (costPerCredit === 0) {
    costPerCredit = 0.20; // Fallback default for free tier
  }
  
  const totalCost = (creditsToBuy * costPerCredit).toFixed(2);
  const totalCostNumber = creditsToBuy * costPerCredit;
  const minimumPurchase = 0.50; // Stripe minimum

  // Handle tutorial restart
  const handleRestartTutorial = async () => {
    const confirmed = window.confirm(
      'Are you sure you want to restart the tutorial? This will reset your tutorial progress and redirect you to the home page to begin the onboarding guide.'
    );

    if (!confirmed) return;

    setIsRestartingTutorial(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await resetTutorial();
      // Navigate to home page to start the tutorial
      navigate('/home');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to restart tutorial';
      setError(errorMessage);
      console.error('Error restarting tutorial:', err);
      setIsRestartingTutorial(false);
    }
  };

  const handleBuyCredits = async () => {
    // Validate minimum purchase amount
    if (totalCostNumber < minimumPurchase) {
      setError(`Minimum purchase amount is €${minimumPurchase.toFixed(2)}. Please increase the number of credits.`);
      return;
    }

    setIsBuyingCredits(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const successUrl = `${window.location.origin}/account?credits_success=true`;
      const cancelUrl = `${window.location.origin}/account?credits_cancelled=true`;

      console.log('Purchasing credits:', { creditsToBuy, successUrl, cancelUrl });

      const response = await subscriptionService.purchaseCredits(
        creditsToBuy,
        successUrl,
        cancelUrl
      );

      console.log('Received checkout response:', response);

      if (!response || !response.checkout_url) {
        throw new Error('No checkout URL received from server');
      }

      console.log('Redirecting to Stripe checkout:', response.checkout_url);

      // Redirect to Stripe checkout
      window.location.href = response.checkout_url;
    } catch (err) {
      console.error('Error purchasing credits:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to purchase credits';
      setError(errorMessage);
      setIsBuyingCredits(false);
    }
  };

  return (
    <div className="account-page">
      <div className="account-container">
        {/* Full-Width Status Bar */}
        <div className={`account-status-bar ${isLowCredits ? 'low-credits' : ''}`}>
          <div className="status-section status-plan">
            <div className="status-label">Current Plan</div>
            <div className="status-value">{subscriptionType}</div>
          </div>
          <div className="status-divider"></div>
          <div className="status-section status-credits">
            <div className="status-label">Credits {isLowCredits && '⚠️'}</div>
            <div className="status-value">
              {subscription.credits_remaining.toLocaleString()} / {subscription.credits_limit.toLocaleString()}
            </div>
            <div className="status-progress">
              <div 
                className={`status-progress-bar ${isLowCredits ? 'low' : ''}`} 
                style={{ width: `${creditsPercentage}%` }}
              ></div>
            </div>
          </div>
          <div className="status-divider"></div>
          <div className="status-section status-billing">
            <div className="status-label">Billing Period Ends</div>
            <div className="status-value">{formatDate(subscription.billing_period_end)}</div>
          </div>
          <div className="status-divider"></div>
          <div className="status-section status-actions">
            <button
              className="status-buy-credits-btn"
              onClick={() => setIsBuyCreditsModalOpen(true)}
              aria-label="Buy more credits"
            >
              Buy Credits
            </button>
            <ThriftLinkCard />
            <button
              className="status-tutorial-btn"
              onClick={handleRestartTutorial}
              disabled={isRestartingTutorial}
              aria-label="Restart tutorial"
              title="Restart the onboarding tutorial"
            >
              {isRestartingTutorial ? 'Restarting...' : 'Restart Tutorial'}
            </button>
            {hasActiveSubscription && (
              <button
                className="status-cancel-btn"
                onClick={handleCancelSubscription}
                disabled={isCancelling}
                aria-label="Cancel subscription"
              >
                {isCancelling ? 'Cancelling...' : 'Cancel Plan'}
              </button>
            )}
          </div>
        </div>

        {/* Buy Credits Modal */}
        {isBuyCreditsModalOpen && (
          <div className="modal-overlay" onClick={() => setIsBuyCreditsModalOpen(false)}>
            <div className="modal-content buy-credits-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Buy Additional Credits</h2>
                <button className="modal-close-btn" onClick={() => setIsBuyCreditsModalOpen(false)}>✕</button>
              </div>
              
              <div className="modal-body">
                <div className="credit-info">
                  <div className="credit-info-item">
                    <span className="credit-info-label">Your Plan:</span>
                    <span className="credit-info-value">{subscriptionType}</span>
                  </div>
                  <div className="credit-info-item">
                    <span className="credit-info-label">Cost per Credit:</span>
                    <span className="credit-info-value">€{costPerCredit.toFixed(2)}</span>
                  </div>
                  <div className="credit-info-item">
                    <span className="credit-info-label">Current Balance:</span>
                    <span className="credit-info-value">{subscription.credits_remaining.toLocaleString()} credits</span>
                  </div>
                </div>

                <div className="credit-purchase-section">
                  <label htmlFor="credits-amount" className="credit-amount-label">
                    Number of Credits to Purchase
                  </label>
                  <div className="credit-amount-controls">
                    <button
                      className="credit-adjust-btn"
                      onClick={() => setCreditsToBuy(Math.max(10, creditsToBuy - 50))}
                      disabled={creditsToBuy <= 10}
                    >
                      -50
                    </button>
                    <input
                      id="credits-amount"
                      type="number"
                      min="10"
                      step="10"
                      value={creditsToBuy}
                      onChange={(e) => setCreditsToBuy(Math.max(10, parseInt(e.target.value) || 10))}
                      className="credit-amount-input"
                    />
                    <button
                      className="credit-adjust-btn"
                      onClick={() => setCreditsToBuy(creditsToBuy + 50)}
                    >
                      +50
                    </button>
                  </div>

                  <div className="credit-presets">
                    <button onClick={() => setCreditsToBuy(100)} className={creditsToBuy === 100 ? 'active' : ''}>100</button>
                    <button onClick={() => setCreditsToBuy(250)} className={creditsToBuy === 250 ? 'active' : ''}>250</button>
                    <button onClick={() => setCreditsToBuy(500)} className={creditsToBuy === 500 ? 'active' : ''}>500</button>
                    <button onClick={() => setCreditsToBuy(1000)} className={creditsToBuy === 1000 ? 'active' : ''}>1,000</button>
                    <button onClick={() => setCreditsToBuy(2500)} className={creditsToBuy === 2500 ? 'active' : ''}>2,500</button>
                  </div>
                </div>

                <div className="credit-total">
                  <span className="credit-total-label">Total Cost:</span>
                  <span className="credit-total-value">€{totalCost}</span>
                </div>
                
                {totalCostNumber < minimumPurchase && (
                  <div className="credit-warning">
                    ⚠️ Minimum purchase is €{minimumPurchase.toFixed(2)}
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button 
                  className="modal-cancel-btn" 
                  onClick={() => setIsBuyCreditsModalOpen(false)}
                  disabled={isBuyingCredits}
                >
                  Cancel
                </button>
                <button 
                  className="modal-confirm-btn" 
                  onClick={handleBuyCredits}
                  disabled={isBuyingCredits || totalCostNumber < minimumPurchase}
                >
                  {isBuyingCredits ? 'Processing...' : `Purchase ${creditsToBuy} Credits`}
                </button>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="error-message" role="alert">
            <span className="error-icon">⚠️</span>
            <span>{error}</span>
            <button
              className="dismiss-btn"
              onClick={() => setError(null)}
              aria-label="Dismiss error"
            >
              ✕
            </button>
          </div>
        )}

        {successMessage && (
          <div className="success-message" role="status">
            <span className="success-icon">✓</span>
            <span>{successMessage}</span>
            <button
              className="dismiss-btn"
              onClick={() => setSuccessMessage(null)}
              aria-label="Dismiss message"
            >
              ✕
            </button>
          </div>
        )}

        {/* Main Content: Subscription Tiers */}
        <div className="tiers-section">
          <div className="tiers-header">
            <h1>Choose Your Plan</h1>
            <p className="tiers-description">
              Select the plan that best fits your needs. Upgrade or change plans anytime.
            </p>
          </div>

          {tiers.length > 0 && (
            <SubscriptionTiers
              tiers={tiers}
              currentTier={subscriptionType}
              onUpgrade={handleUpgrade}
              isUpgrading={isUpgrading}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default AccountPage;
